/** The quick actions every value on an IDENTITY surface offers — the user
 *  detail page and its tabs, the sessions grid, the OTA page, and the RBAC
 *  dialogs — so the same reference (a user, an email, an OTA, an access group,
 *  an operator) gets the same set wherever it is rendered.
 *
 *  Every href comes from gridLinks; every action from ValueActions' `act`.
 *  Id spaces (see gridLinks): a user is `user=#<id>` on /bookings, /orders and
 *  /tags (the id-match sigil — the plain form matches by email/name), the
 *  all-digits `user=<id>` on /delivery-logs, and `user_id=<id>` on
 *  /import-runs; the plain-param grids take `?user=<id>` (sessions),
 *  `?user=<email>` (bgtasks) and `?actor_email=` / `?target_email=` (audit).
 *  An access group is `agroup=#<id>` on /bookings — there is no `agroup`
 *  filter on the users grids. An OTA is the all-digits `ota=<id>` on
 *  /notifications/features; `reseller=` on /bookings is the restech's
 *  free-text ref, NOT the OTA, so it is never built here.
 */
import type { OperatorRef } from '@/components/ValueActions/identity';
import { operatorActions } from '@/components/ValueActions/identity';
import { act, type ValueAction } from '@/components/ValueActions/ValueActions';
import type { CellAction } from '@/components/DataGrid/CellActionsPopover';
import { externalUrl, looksLikeUrl } from '@/utils/externalUrl';
import {
  accessGroupsHref,
  auditHref,
  bgtasksHref,
  bookingsHref,
  changesHref,
  cond,
  consoleRouteFor,
  deliveryLogsHref,
  featureFlagsHref,
  idCond,
  importRunsHref,
  ordersHref,
  otasHref,
  sessionsHref,
  tagsHref,
  usersHref,
  type Tenant,
  type UserKind,
} from '@/utils/gridLinks';

export interface UserCtx {
  /** Scopes the grid links (`?tenant=`). */
  tenantId: Tenant;
  /** Which users grid this user lives on (`/users/<kind>`). Default guests. */
  kind?: UserKind;
}

const tenantOf = (ctx: UserCtx) => ctx.tenantId ?? null;
const kindOf = (ctx: UserCtx) => ctx.kind ?? 'guests';

/** The users-grid path segment for an API user kind (`guest` → `guests`). */
export function gridKindOf(kind: 'system' | 'staff' | 'guest'): UserKind {
  return kind === 'guest' ? 'guests' : kind;
}

// ---- The user itself -------------------------------------------------------

export interface RelatedUser {
  id: number;
  email: string;
}

/** The related-records strip: every grid that can list this user's records,
 *  as filter actions. Audit links are gated on the viewer's audit permission
 *  (the /audit route refuses without it). `tabs` — the page tabs the viewer sees:
 *  a chip that only repeats one of them (Bookings, Sessions, Audit about the user)
 *  is dropped. */
export function relatedRecordActions(
  user: RelatedUser,
  ctx: UserCtx,
  opts: { canViewAudit: boolean; tabs?: ReadonlySet<string> },
): CellAction[] {
  const t = tenantOf(ctx);
  const id = String(user.id);
  const besideTab = (tab: string) => opts.tabs?.has(tab) ?? false;
  const out: CellAction[] = [
    ...(besideTab('bookings') ? [] : [act.filter(bookingsHref([idCond('user', user.id)], t), 'Bookings')]),
    act.filter(ordersHref([idCond('user', user.id)], t), 'Orders'),
    act.filter(deliveryLogsHref([cond('user', id)], { tenant: t }), 'Delivery logs (7 days)'),
    act.filter(importRunsHref([cond('user_id', id, 'exact')], t), 'Import runs'),
    ...(besideTab('sessions') ? [] : [act.filter(sessionsHref({ user: user.id }), 'Sessions')]),
    act.filter(bgtasksHref({ user: user.email }), 'Background tasks'),
    act.filter(tagsHref([idCond('user', user.id)], t), 'Tags'),
  ];
  if (opts.canViewAudit) {
    if (!besideTab('audit')) out.push(act.filter(auditHref({ target_email: user.email }), 'Audit (about)'));
    out.push(act.filter(auditHref({ actor_email: user.email }), 'Audit (as actor)'));
  }
  return out;
}

/** A reference to another user (an audit actor, a group member): open their
 *  page, mail, copy, and their bookings / sessions. */
export function userRefActions(user: { id: number; email: string }, ctx: UserCtx): ValueAction[] {
  return [
    act.open(`/users/${user.id}`, 'Open user'),
    act.mailto(user.email),
    act.copy(user.email, 'Copy email'),
    act.filter(bookingsHref([idCond('user', user.id)], tenantOf(ctx)), 'Bookings of this user'),
    act.filter(sessionsHref({ user: user.id }), 'Sessions of this user'),
  ];
}

/** `internal.trip_count` — the bookings it counts. */
export function tripCountActions(userId: number, ctx: UserCtx): ValueAction[] {
  return [act.filter(bookingsHref([idCond('user', userId)], tenantOf(ctx)), 'Bookings of this user')];
}

// ---- Contact values ---------------------------------------------------------

export function emailActions(email: string, ctx: UserCtx): ValueAction[] {
  const t = tenantOf(ctx);
  return [
    act.mailto(email),
    act.copy(email, 'Copy email'),
    act.filter(bookingsHref([cond('email', email)], t), 'Bookings with this email'),
    act.filter(deliveryLogsHref([cond('email', email)], { tenant: t }), 'Delivery logs to this email (7 days)'),
    act.filter(usersHref(kindOf(ctx), { tenant: t, email_contains: email }), 'Users with this email'),
  ];
}

export function phoneActions(phone: string, ctx: UserCtx): ValueAction[] {
  return [
    act.tel(phone),
    act.copy(phone, 'Copy phone'),
    act.filter(bookingsHref([cond('phone', phone)], tenantOf(ctx)), 'Bookings with this phone'),
  ];
}

/** A user-typed URL (a URL contact): opens as an absolute address — a scheme-less one as
 *  https, never a router link to `/example.com/…` — and copies as typed; something that is
 *  no web address only copies. */
export function urlActions(url: string, label = 'Open link'): ValueAction[] {
  return [...(looksLikeUrl(url) ? [act.external(externalUrl(url), label)] : []), act.copy(url, 'Copy URL')];
}

/** One row of the Contacts tab (`type` 0 = email, 1 = phone, 2 = URL). */
export function contactActions(
  contact: { type: number; contact: string },
  ctx: UserCtx,
): ValueAction[] {
  const value = contact.contact.trim();
  if (!value) return [];
  if (contact.type === 0) return emailActions(value, ctx);
  if (contact.type === 1) return phoneActions(value, ctx);
  if (contact.type === 2 && /^https?:\/\/\S+$/i.test(value)) return urlActions(value);
  return [act.copy(value, 'Copy value')];
}

/** A contact that is only worth mailing / calling — an OTA's support address,
 *  which is not a console user and has no records of its own. */
export function plainEmailActions(email: string): ValueAction[] {
  return [act.mailto(email), act.copy(email, 'Copy email')];
}

export function plainPhoneActions(phone: string): ValueAction[] {
  return [act.tel(phone), act.copy(phone, 'Copy phone')];
}

// ---- OTA ---------------------------------------------------------------------

/** An OTA reference (a user's attributing agency, a contact's OTA). */
export function otaActions(ota: { id: number; name?: string | null }): ValueAction[] {
  const out: ValueAction[] = [
    act.open(`/otas/${ota.id}`, 'Open OTA'),
    act.filter(featureFlagsHref([cond('ota', String(ota.id))]), 'Feature flags of this OTA'),
  ];
  if (ota.name) out.push(act.copy(ota.name, 'Copy OTA name'));
  out.push(act.copy(String(ota.id), 'Copy OTA id'));
  return out;
}

/** The OTA page's own id: copy, and the flags keyed on it. */
export function otaIdActions(id: number): ValueAction[] {
  return [
    act.copy(String(id), 'Copy id'),
    act.filter(featureFlagsHref([cond('ota', String(id))]), 'Feature flags of this OTA'),
  ];
}

export function otaEnabledActions(enabled: boolean): ValueAction[] {
  return [act.filter(otasHref({ enabled }), enabled ? 'Enabled OTAs' : 'Disabled OTAs')];
}

// ---- RBAC --------------------------------------------------------------------

/** An access group: its page, the bookings of its members, copy. */
export function accessGroupActions(
  group: { id: number; name?: string | null },
  tenantId: Tenant,
): ValueAction[] {
  const out: ValueAction[] = [
    act.open(`/rbac/access-groups/${group.id}`, 'Open access group'),
    act.filter(bookingsHref([idCond('agroup', group.id)], tenantId), 'Bookings of members of this group'),
  ];
  if (group.name) out.push(act.copy(group.name, 'Copy group name'));
  out.push(act.copy(String(group.id), 'Copy group id'));
  return out;
}

/** The group's OWN header, inside its editor: everything `accessGroupActions`
 *  offers except "open access group", which is where the reader already is. */
export function accessGroupSelfActions(
  group: { id: number; name?: string | null },
  tenantId: Tenant,
): ValueAction[] {
  return accessGroupActions(group, tenantId).filter(
    (a) => !('key' in a && a.key.startsWith('open:/rbac/access-groups/')),
  );
}

/** A bare access-group id (materialise results, engine warnings). */
export function accessGroupIdActions(id: number): ValueAction[] {
  return [act.open(`/rbac/access-groups/${id}`, 'Open access group'), act.copy(String(id), 'Copy group id')];
}

export function managedGroupsActions(): ValueAction[] {
  return [act.filter(accessGroupsHref({ managed: true }), 'Managed groups')];
}

/** The operator of an RBAC / identity row: open it, its staff, copy its code. */
export function rbacOperatorActions(tenant: OperatorRef): ValueAction[] {
  return operatorActions(tenant, {
    href: usersHref('staff', { tenant: tenant.id }),
    label: 'Staff users of this operator',
  });
}

/** A console (Django) group membership chip → the system users in it. The
 *  users grid's `?group=` takes the group ID (UsersListPage feeds the select
 *  `String(g.id)`), not its name. */
export function consoleGroupActions(group: { id: number; name: string }): ValueAction[] {
  return [
    act.filter(usersHref('system', { group: group.id }), `System users in "${group.name}"`),
    act.copy(group.name, 'Copy group name'),
  ];
}

/** An access-scope tag from the dashboard permissions table (`tagmap`). */
export function permissionTagActions(tag: { id: number; name: string }): ValueAction[] {
  return [act.open(`/tags/${tag.id}`, 'Open tag'), act.copy(tag.name, 'Copy name')];
}

// ---- Sessions ----------------------------------------------------------------

/** The Booking column of the sessions grid: the booking's page, and every
 *  session issued for it. `/users/sessions?booking=` takes the console pk,
 *  which is what `SessionRow.booking.id` is. */
export function sessionBookingActions(booking: { id: number; label?: string }): ValueAction[] {
  const out: ValueAction[] = [
    act.open(`/bookings/${booking.id}`, 'Open booking'),
    act.filter(sessionsHref({ booking: booking.id }), 'Sessions of this booking'),
  ];
  if (booking.label) out.push(act.copy(booking.label, 'Copy label'));
  out.push(act.copy(String(booking.id), 'Copy booking id'));
  return out;
}

// ---- Audit tab ---------------------------------------------------------------

export interface AuditCtx {
  /** The user whose audit tab this is (the `target_email` of every row). */
  userEmail: string;
  canViewAudit: boolean;
  canOpenUsers: boolean;
}

/** The row itself: the audit entry's own page, plus the tenant / task /
 *  changed-object references the grid has no column for. */
export function auditRowActions(
  row: {
    id: number;
    tenant?: OperatorRef | null;
    task_id?: string | null;
    linked_object?: { content_type: string; object_id: number } | null;
  },
  ctx: AuditCtx,
): ValueAction[] {
  const out: ValueAction[] = [act.open(`/audit/${row.id}`, 'Open audit entry')];
  if (row.tenant) out.push(...auditTenantActions(row.tenant, ctx));
  if (row.task_id) out.push(...auditTaskActions(row.task_id, ctx));
  if (row.linked_object) {
    const to = consoleRouteFor(row.linked_object.content_type, row.linked_object.object_id);
    if (to) out.push(act.open(to, 'Open the changed record'));
  }
  return out;
}

export function auditEventActions(event: string, ctx: AuditCtx): ValueAction[] {
  const out: ValueAction[] = [act.copy(event, 'Copy event')];
  if (ctx.canViewAudit) {
    out.push(
      act.filter(
        auditHref({ target_email: ctx.userEmail, event_contains: event }),
        'This event about this user (audit grid)',
      ),
    );
  }
  return out;
}

export function auditActorActions(actor: { id: number; email: string }, ctx: AuditCtx): ValueAction[] {
  const out: ValueAction[] = [];
  if (ctx.canOpenUsers) out.push(act.open(`/users/${actor.id}`, 'Open user'));
  out.push(act.mailto(actor.email), act.copy(actor.email, 'Copy email'));
  if (ctx.canViewAudit) out.push(act.filter(auditHref({ actor_email: actor.email }), 'Audit entries by this user'));
  return out;
}

/** An IP: /audit has no `ip` param — `geo=` is the one that searches it
 *  (it matches the IP, city and country columns). */
export function auditIpActions(ip: string, ctx: AuditCtx): ValueAction[] {
  const out: ValueAction[] = [act.copy(ip, 'Copy IP'), act.whois(ip)];
  if (ctx.canViewAudit) out.push(act.filter(auditHref({ geo: ip }), 'Audit entries from this IP'));
  return out;
}

export function auditTenantActions(tenant: OperatorRef, ctx: AuditCtx): ValueAction[] {
  const out: ValueAction[] = [act.open(`/tour-operators/${tenant.id}`, 'Open operator')];
  if (ctx.canViewAudit) out.push(act.filter(auditHref({ tenant: tenant.id }), 'Audit entries of this operator'));
  if (tenant.code) out.push(act.copy(tenant.code, 'Copy operator code'));
  return out;
}

/** The background task behind an audit row. `AuditLogEntry.task_id` IS a
 *  BackgroundTaskRecord id (the audit dialog links it the same way) — unlike
 *  `Import_Run.task_id`, which is a Celery id and must never be linked here. */
export function auditTaskActions(taskId: string, ctx: AuditCtx): ValueAction[] {
  const out: ValueAction[] = [
    act.open(`/bgtasks/${taskId}`, 'Open the background task'),
    act.copy(taskId, 'Copy task id'),
  ];
  if (ctx.canViewAudit) {
    out.push(
      act.filter(auditHref({ task_id: taskId }), 'Audit trail of this task'),
      act.filter(changesHref({ task_id: taskId }), 'Record changes by this task'),
    );
  }
  return out;
}
